import { Phone, MessageCircle, Mail, MapPin, Navigation } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import EnquiryForm from "@/components/EnquiryForm";
import { BRAND } from "@/lib/brand";
import { telLink, mailLink, generalWhatsapp } from "@/lib/whatsapp";
import { useSeo } from "@/lib/useSeo";

const QUICK = [
  { label: "Call BIOSYN", icon: Phone, href: telLink(), color: "#087FBF", testid: "contact-call" },
  { label: "WhatsApp BIOSYN", icon: MessageCircle, href: generalWhatsapp(), color: "#25D366", testid: "contact-whatsapp", external: true },
  { label: "Email BIOSYN", icon: Mail, href: mailLink("Product Enquiry - BIOSYN Website"), color: "#16B8D4", testid: "contact-email" },
  { label: "Get Directions", icon: Navigation, href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BRAND.mapsQuery)}`, color: "#3FAE35", testid: "contact-directions", external: true },
];

export default function Contact() {
  useSeo({
    title: "Contact BIOSYN | Aquaculture Solutions & Technical Support",
    description: "Connect with BIOSYN for product information, technical guidance and aquaculture solutions. Call, WhatsApp or email our team.",
  });

  return (
    <div>
      <section style={{ background: "#063B63" }} className="py-16 md:py-20">
        <div className="container-x">
          <SectionHeading light overline="Contact Us" title="Connect With BIOSYN"
            subtitle="For product information, technical guidance and aquaculture solutions, contact our team." />
        </div>
      </section>

      <section className="container-x -mt-8 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK.map((q) => (
            <a key={q.label} href={q.href} data-testid={q.testid}
              {...(q.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="biosyn-card p-5 flex flex-col items-center text-center gap-3 hover:!-translate-y-1">
              <span className="w-12 h-12 rounded-full flex items-center justify-center text-white" style={{ background: q.color }}>
                <q.icon className="w-6 h-6" />
              </span>
              <span className="font-heading font-bold text-sm" style={{ color: "#063B63" }}>{q.label}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="section container-x">
        <div className="grid lg:grid-cols-5 gap-10">
          {/* Details */}
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-extrabold" style={{ color: "#063B63" }}>BIOSYN</h2>
            <p className="text-sm font-semibold tracking-wide mt-1" style={{ color: "#16B8D4" }}>ADVANCED AQUACULTURE SOLUTIONS</p>

            <div className="mt-8 space-y-5">
              <div className="flex gap-4">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(8,127,191,0.1)" }}><MapPin className="w-5 h-5" style={{ color: "#087FBF" }} /></span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: "#063B63" }}>Address</p>
                  <p className="text-sm mt-0.5" style={{ color: "#4A6B82" }}>Auto Nagar, Belagavi (Belgaum),<br />Karnataka – 590015, India</p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(8,127,191,0.1)" }}><Phone className="w-5 h-5" style={{ color: "#087FBF" }} /></span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: "#063B63" }}>Phone</p>
                  <a href={telLink()} className="text-sm mt-0.5 hover:underline block" style={{ color: "#4A6B82" }}>{BRAND.phone}</a>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(8,127,191,0.1)" }}><Mail className="w-5 h-5" style={{ color: "#087FBF" }} /></span>
                <div>
                  <p className="font-semibold text-sm" style={{ color: "#063B63" }}>Email</p>
                  <a href={mailLink()} className="text-sm mt-0.5 hover:underline block break-all" style={{ color: "#4A6B82" }}>{BRAND.email}</a>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-2xl overflow-hidden biosyn-card !p-0 h-56">
              <iframe
                title="BIOSYN location"
                width="100%" height="100%" loading="lazy"
                style={{ border: 0 }}
                src={`https://www.google.com/maps?q=${encodeURIComponent(BRAND.mapsQuery)}&output=embed`}
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 biosyn-card p-6 md:p-8">
            <h3 className="font-heading text-2xl font-extrabold mb-1" style={{ color: "#063B63" }}>Send an Enquiry</h3>
            <p className="text-sm mb-6" style={{ color: "#4A6B82" }}>Fill in the form and our technical team will get back to you shortly.</p>
            <EnquiryForm source="CONTACT_FORM" showQuantity />
          </div>
        </div>
      </section>
    </div>
  );
}
